import React, { useState } from "react";
import { Row, Col, Collapse } from "antd";
import { useTranslation } from "react-i18next";
import { DynamicFormField } from "./DynamicFormField";
import type { DynamicFormSectionProps } from "./types";
import { useResponsive } from "../../hooks";

/**
 * Form section component that renders a group of fields in a card
 * Supports Aras Responsive Form layout properties (title, bordered, columns up to 4)
 * Adapts to mobile/tablet screens automatically
 */
export const DynamicFormSection: React.FC<DynamicFormSectionProps> = ({
  config,
  fields,
  form,
  disabled = false,
  readOnly = false,
  customRenderers,
  defaultCollapsed = false,
}) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useResponsive();

  // Get number of columns - responsive override
  const configColumns = config.columns || 2;
  // Mobile: always 1 column, Tablet: max 2 columns, Desktop: use config
  const columns = isMobile
    ? 1
    : isTablet
      ? Math.min(configColumns, 2)
      : configColumns;
  const colSpan = Math.floor(24 / columns);

  // Filter fields that belong to this section
  const sectionFields = fields.filter((field) =>
    config.fields.includes(field.mappedName),
  );

  // If no fields match, don't render the section
  if (sectionFields.length === 0) {
    return null;
  }

  // Sort fields by their position in config.fields array
  const sortedFields = [...sectionFields].sort((a, b) => {
    const indexA = config.fields.indexOf(a.mappedName);
    const indexB = config.fields.indexOf(b.mappedName);
    return indexA - indexB;
  });

  // Get title - prefer direct title from Aras, otherwise use i18n key
  // For Responsive Form sections, titleKey is empty so we only use config.title
  const title = config.title || (config.titleKey ? t(config.titleKey) : "");

  // Track expanded state for collapsible groupboxes
  const [activeKey, setActiveKey] = useState<string[]>(
    defaultCollapsed ? [] : ["main"],
  );

  // Build a map of field name to field metadata for quick lookup
  const fieldMap = new Map(fields.map((f) => [f.mappedName, f]));

  // Helper to render a single field
  const renderField = (fieldName: string) => {
    const field = fieldMap.get(fieldName);
    if (!field) return null;
    // Merge section-level disabled with per-field disabled from Aras form layout
    const fieldDisabled = disabled || field.isDisabledByForm === true;
    const CustomRenderer = customRenderers?.[field.mappedName];
    return CustomRenderer ? (
      <CustomRenderer
        key={field.mappedName}
        field={field}
        form={form}
        disabled={fieldDisabled}
        readOnly={readOnly}
      />
    ) : (
      <DynamicFormField
        key={field.mappedName}
        field={field}
        form={form}
        disabled={fieldDisabled}
        readOnly={readOnly}
      />
    );
  };

  // Responsive gutter - less on mobile
  const gutter = isMobile ? 0 : 16;

  // Render fields - shared between Card and non-Card layouts
  const renderFieldsContent = () => {
    // If we have rows (from Responsive Form with mixed pattern), render row by row
    if (config.rows && config.rows.length > 0) {
      return (
        <>
          {config.rows.map((rowFields, rowIndex) => {
            // Calculate column span based on number of fields in this row
            // Count all positions (including empty ones) for consistent column widths
            const fieldsInRow = rowFields.length;
            // For single non-empty field without empty positions, use full width
            // Otherwise use equal column widths based on position count
            const nonEmptyCount = rowFields.filter((f) => f !== "").length;

            // Responsive row column span
            let rowColSpan: number;
            if (isMobile) {
              rowColSpan = 24; // Full width on mobile
            } else if (nonEmptyCount === 1 && fieldsInRow === 1) {
              rowColSpan = 24;
            } else if (isTablet) {
              // On tablet, max 2 columns per row
              rowColSpan = Math.max(
                12,
                Math.floor(24 / Math.min(fieldsInRow, 2)),
              );
            } else {
              rowColSpan = Math.floor(24 / fieldsInRow);
            }

            return (
              <Row key={`row-${rowIndex}`} gutter={gutter}>
                {rowFields.map((fieldName, colIndex) => (
                  <Col
                    key={fieldName || `empty-${rowIndex}-${colIndex}`}
                    xs={24}
                    sm={24}
                    md={nonEmptyCount === 1 ? 24 : 12}
                    lg={rowColSpan}
                  >
                    {/* Render field or empty space for empty positions */}
                    {fieldName ? renderField(fieldName) : null}
                  </Col>
                ))}
              </Row>
            );
          })}
        </>
      );
    }

    // If we have columnFields (from Responsive Form), render by column structure
    if (config.columnFields && config.columnFields.length > 0) {
      return (
        <Row gutter={gutter}>
          {config.columnFields.map((columnFieldNames, colIndex) => (
            <Col
              key={`col-${colIndex}`}
              xs={24}
              sm={24}
              md={isTablet ? Math.max(12, colSpan) : colSpan}
              lg={colSpan}
            >
              {columnFieldNames.map((fieldName) => renderField(fieldName))}
            </Col>
          ))}
        </Row>
      );
    }

    // Fallback: render fields in order (static config)
    return (
      <Row gutter={gutter}>
        {sortedFields.map((field) => (
          <Col
            key={field.mappedName}
            xs={24}
            sm={24}
            md={isTablet ? Math.max(12, colSpan) : colSpan}
            lg={colSpan}
          >
            {renderField(field.mappedName)}
          </Col>
        ))}
      </Row>
    );
  };

  // For Responsive Form columnsets without title/border: render just the content
  // This matches Aras display where columnsets are invisible layout containers
  // Use == null to check for both null and undefined (API may return null)
  if (!title && config.bordered == null) {
    return <>{renderFieldsContent()}</>;
  }

  // For bordered sections (group boxes) or sections with titles: use Collapse
  // This allows auto-collapsing from 2nd groupbox onwards
  const collapseStyle: React.CSSProperties = {
    marginBottom: isMobile ? 12 : 16,
    background: "transparent",
  };

  // Style for non-bordered sections (invisible groupbox with just title)
  const nonBorderedStyle: React.CSSProperties = {
    marginBottom: isMobile ? 12 : 16,
    border: "none",
    boxShadow: "none",
  };

  // Use Collapse for collapsible groupboxes
  return (
    <Collapse
      activeKey={activeKey}
      onChange={(keys) => setActiveKey(keys as string[])}
      style={config.bordered === false ? nonBorderedStyle : collapseStyle}
      size={isMobile ? "small" : "middle"}
      items={[
        {
          key: "main",
          label: title || undefined,
          children: renderFieldsContent(),
        },
      ]}
    />
  );
};
